import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, Receipt, Plus, Video, ExternalLink, Construction, X as XIcon, Search } from "lucide-react";

// ---- Searchable multi-select for meeting invitees ----
function MeetingInvitees({ people, invitees, onToggle }) {
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(invitees), [invitees]);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (people || []).filter((p) => {
      if (!query) return true;
      return (p.name || "").toLowerCase().includes(query) || (p.email || "").toLowerCase().includes(query);
    });
  }, [people, q]);
  const selected = (people || []).filter((p) => selectedSet.has(p.user_id));

  return (
    <div className="mt-2 border border-slate-200 rounded-sm bg-white">
      {/* Selected pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 border-b border-slate-200 bg-slate-50">
          {selected.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1 text-[11px] bg-kb-primary text-white px-2 py-0.5 rounded-sm" data-testid={`meeting-selected-${p.user_id}`}>
              {p.name || p.email}
              <button type="button" onClick={() => onToggle(p.user_id)} className="hover:opacity-70">
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Search box */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-kb-ink-light absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          className="kb-input w-full pl-8 text-sm border-0 focus:ring-0"
          placeholder={people.length === 0 ? "No teammates available yet" : "Search teammates by name or email…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="meeting-invitee-search"
        />
      </div>
      {/* Result list */}
      {q && (
        <div className="max-h-40 overflow-y-auto border-t border-slate-200">
          {filtered.length === 0 ? (
            <div className="text-xs text-kb-ink-muted text-center py-4">No matches.</div>
          ) : filtered.map((p) => {
            const isOn = selectedSet.has(p.user_id);
            return (
              <button
                key={p.user_id}
                type="button"
                onClick={() => onToggle(p.user_id)}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between text-sm border-b border-slate-100 last:border-0 ${isOn ? "bg-kb-primary/5" : ""}`}
                data-testid={`meeting-invitee-${p.user_id}`}
              >
                <div className="min-w-0">
                  <div className="text-kb-ink truncate">{p.name || p.email}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{p.email}</div>
                </div>
                <span className={`kb-mono text-[10px] uppercase tracking-wider ${isOn ? "text-kb-primary" : "text-kb-ink-light"}`}>{isOn ? "Selected" : "Select"}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="px-3 py-2 kb-mono text-[10px] text-kb-ink-muted border-t border-slate-200 bg-slate-50">
        {selected.length} invitee{selected.length === 1 ? "" : "s"} · {people.length} teammate{people.length === 1 ? "" : "s"} available
      </div>
    </div>
  );
}

// ============================ ATTENDANCE ============================
export function EmployeeAttendance({ userId = null, title = null, embedded = false } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // date -> bool
  useEffect(() => {
    const q = userId ? `?days=30&user_id=${encodeURIComponent(userId)}` : "?days=30";
    api.get(`/employee/attendance${q}`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, [userId]);
  const tfmt = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const hfmt = (mins) => `${Math.floor((mins || 0) / 60)}h ${(mins || 0) % 60}m`;
  return (
    <div data-testid="employee-attendance-page">
      {!embedded && <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE</div>}
      {!embedded && <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">{title || <>Attendance · <em className="italic text-kb-primary">last 30 days</em></>}</h1>}
      {!embedded && <p className="text-sm text-kb-ink-muted mt-2">Multi-session aware — each clock-in / clock-out pair is counted toward the day&apos;s worked duration.</p>}
      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? <div className="mt-8 text-sm text-kb-ink-muted">No attendance records yet.</div> :
        (
          <div className="mt-8 border border-slate-200 rounded-sm bg-white overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-slate-200 bg-slate-50 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
              <div className="col-span-3">Date</div>
              <div className="col-span-2">First in</div>
              <div className="col-span-2">Last out</div>
              <div className="col-span-2">Sessions</div>
              <div className="col-span-3">Worked</div>
            </div>
            {items.map((d) => {
              const isOpen = !!expanded[d.date];
              const sessions = d.sessions || [];
              const worked = d.worked_minutes ?? d.minutes_online ?? 0;
              return (
                <div key={d.date} className="border-b border-slate-200 last:border-0" data-testid={`att-row-${d.date}`}>
                  <button
                    type="button"
                    onClick={() => sessions.length > 0 && setExpanded((m) => ({ ...m, [d.date]: !m[d.date] }))}
                    className={`w-full grid grid-cols-12 px-4 py-3 text-sm text-left ${sessions.length > 0 ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
                    data-testid={`att-row-${d.date}-toggle`}
                  >
                    <div className="col-span-3 font-mono">{d.date}</div>
                    <div className="col-span-2 kb-mono text-[11px] text-kb-ink-muted">{tfmt(d.clock_in)}</div>
                    <div className="col-span-2 kb-mono text-[11px] text-kb-ink-muted">{tfmt(d.clock_out)}</div>
                    <div className="col-span-2 kb-mono text-[11px] text-kb-ink-muted">{sessions.length > 0 ? `${sessions.length}${sessions.length > 1 ? " ↓" : (isOpen ? " ↑" : " ↓")}` : "—"}</div>
                    <div className="col-span-3 font-mono text-kb-primary">{hfmt(worked)}</div>
                  </button>
                  {isOpen && sessions.length > 0 && (
                    <div className="px-4 pb-3 bg-slate-50" data-testid={`att-row-${d.date}-sessions`}>
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1 mb-2">Sessions today</div>
                      <div className="space-y-1">
                        {sessions.map((s, idx) => (
                          <div key={idx} className="grid grid-cols-12 text-[11px] font-mono text-kb-ink">
                            <div className="col-span-3 text-kb-ink-muted">#{idx + 1}</div>
                            <div className="col-span-3">{tfmt(s.in)}</div>
                            <div className="col-span-3">{s.out ? tfmt(s.out) : <span className="text-amber-600">open</span>}</div>
                            <div className="col-span-3 text-kb-primary">{hfmt(s.minutes)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ============================ PAYSLIPS ============================
export function EmployeePayslips() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/employee/payslips").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="employee-payslips-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE · ERP</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Payslips</h1>
      <p className="text-sm text-kb-ink-muted mt-2">Auto-computed from tracked hours at NPR 250/hour. Admin finalises payroll monthly.</p>
      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? <div className="mt-8 text-sm text-kb-ink-muted">No payslips yet — once you&apos;ve logged time, the monthly total appears here.</div> :
        (
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <div key={p.month} className="kb-card p-5" data-testid={`payslip-${p.month}`}>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1">
                  <Receipt className="w-3 h-3" /> {p.month}
                </div>
                <div className="mt-2 font-serif text-2xl text-kb-ink">NPR {p.gross_npr.toLocaleString()}</div>
                <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">{p.hours} h · @ NPR {p.rate_npr_per_hour}/h</div>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm mt-3 inline-block">{p.status}</div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ============================ MEETINGS ============================
export function EmployeeMeetings() {
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState(30);
  const [recurrence, setRecurrence] = useState("none");
  const [days, setDays] = useState([]); // e.g. ["MO","WE","FR"] when recurrence === "custom"
  const [invitees, setInvitees] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/employee/meetings").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/employee/assignable").then((r) => setPeople(r.data || [])).catch(() => {});
  }, []);
  const toggleInvitee = (uid) => setInvitees((arr) => arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid]);
  const toggleDay = (d) => setDays((arr) => arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d]);
  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (recurrence !== "none" && !when) { toast.error("Recurring meetings need a start date/time"); return; }
    if (recurrence === "custom" && days.length === 0) { toast.error("Pick at least one weekday"); return; }
    // For "custom", translate the day chips to a raw RRULE the backend already
    // understands (see `_build_rrule` — accepts `RRULE:` prefixed strings).
    const DAY_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
    const rec = recurrence === "custom"
      ? `RRULE:FREQ=WEEKLY;BYDAY=${DAY_ORDER.filter((d) => days.includes(d)).join(",")}`
      : recurrence;
    setBusy(true);
    try {
      const r = await api.post("/employee/meetings", {
        title: title.trim(),
        when,
        duration_minutes: Number(duration) || 30,
        recurrence: rec,
        invitee_user_ids: invitees,
      });
      toast.success(r.data?.meet_url ? `Meeting created · ${invitees.length} invitee(s) emailed` : "Meeting created (no meet link)");
      setTitle(""); setWhen(""); setRecurrence("none"); setDays([]); setInvitees([]); load();
      window.dispatchEvent(new CustomEvent("meetings:refresh"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="employee-meetings-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Meetings</h1>
      <p className="text-sm text-kb-ink-muted mt-2">Create instant Google Meet rooms (Daily.co fallback if Google isn&apos;t connected) or schedule recurring meetings that land on every invitee&apos;s Google Calendar.</p>

      <form onSubmit={create} className="kb-card p-5 mt-6 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_auto] items-end">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title</label>
            <input required className="kb-input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sprint kickoff" data-testid="meeting-title" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">When {recurrence !== "none" ? "(required for recurring)" : "(optional)"}</label>
            <input type="datetime-local" className="kb-input mt-1" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="meeting-when" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Duration (min)</label>
            <input type="number" min="10" step="5" className="kb-input mt-1" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="meeting-duration" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Recurrence</label>
            <select className="kb-input mt-1" value={recurrence} onChange={(e) => setRecurrence(e.target.value)} data-testid="meeting-recurrence">
              <option value="none">One-off</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom days</option>
            </select>
            {recurrence === "custom" && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="meeting-recurrence-days">
                {[
                  { k: "MO", label: "Mon" }, { k: "TU", label: "Tue" }, { k: "WE", label: "Wed" },
                  { k: "TH", label: "Thu" }, { k: "FR", label: "Fri" }, { k: "SA", label: "Sat" }, { k: "SU", label: "Sun" },
                ].map((d) => (
                  <button
                    key={d.k}
                    type="button"
                    onClick={() => toggleDay(d.k)}
                    className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${days.includes(d.k) ? "bg-kb-primary text-white border-kb-primary" : "border-slate-300 text-kb-ink-muted hover:border-kb-primary"}`}
                    data-testid={`meeting-day-${d.k}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={busy} className="kb-btn-primary text-sm" data-testid="meeting-create-btn">
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Create"}
          </button>
        </div>
        <div>
          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Invite teammates (optional)</label>
          <MeetingInvitees
            people={people}
            invitees={invitees}
            onToggle={toggleInvitee}
          />
        </div>
      </form>

      <h2 className="font-serif text-xl text-kb-ink mt-8">Recent</h2>
      {items.length === 0 ? <div className="mt-3 text-sm text-kb-ink-muted">No meetings yet.</div> :
        (
          <div className="mt-3 space-y-2">
            {items.map((m) => (
              <div key={m.id} className="kb-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`meeting-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-kb-ink">{m.title} {m.recurrence && <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary bg-kb-subtle px-1.5 py-0.5 rounded-sm ml-1">recurring</span>}</div>
                  <div className="kb-mono text-[11px] text-kb-ink-muted">{m.when ? new Date(m.when).toLocaleString() : "Instant meeting"} · {m.provider || "—"}{m.duration_minutes ? ` · ${m.duration_minutes} min` : ""}</div>
                </div>
                {m.meet_url && (
                  <a href={m.meet_url} target="_blank" rel="noreferrer" className="kb-btn-primary text-xs" data-testid={`meeting-${m.id}-join`}>
                    <Video className="w-3.5 h-3.5" /> Join <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ============================ ERP placeholder pages ============================
function StubPage({ title, icon: Icon = Construction, hint }) {
  return (
    <div data-testid={`erp-stub-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE · ERP</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">{title}</h1>
      <div className="kb-card p-10 mt-8 text-center">
        <Icon className="w-10 h-10 text-kb-ink-light mx-auto" strokeWidth={1.5} />
        <h2 className="font-serif text-xl text-kb-ink mt-4">Coming soon</h2>
        <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">{hint}</p>
      </div>
    </div>
  );
}
export function ErpLeaves() { return <StubPage title="Leaves" icon={Calendar} hint="Apply for and track approved time off. Backend wiring lands in the next sprint." />; }
export function ErpDocuments() { return <StubPage title="Documents" hint="Offer letter, NDA, ID copies and payroll documents will live here." />; }
export function ErpExpenses() { return <StubPage title="Expenses" icon={Clock} hint="Submit, approve and reconcile business expenses." />; }
